import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { TranslationProvider } from 'react-translate.ts';
import RequestService, { RETRY_LIMIT_ERROR } from '@tcc-online/request-service';
import {
    captureException,
    captureMessage,
    feedbackIntegration,
} from '@sentry/react';
import { Theme, useTheme } from '@tcc-online/react-theme-provider';
import '@fontsource/nunito/400.css';
import '@fontsource/nunito/800.css';

import { AppRoutes } from './components/routes/AppRoutes';
import { Navbar } from './components';
import NotificationList from './common/components/notifications/NotificationList/NotificationList';
import AuthSelectors from './selectors/AuthSelectors';
import { AuthActions } from './actions';
import './theme/sass/app.scss';
import {
    TranslationsProvider,
    useTranslations,
} from '@tcc-online/use-translations';
import { Loading, LoadingOverlay } from '@tcc-online/common-components';
import QuestionnaireTimer from './components/QuestionnaireTimer/QuestionnaireTimer';
import { QUESTIONNAIRE_PATH, SIGN_IN_PATH } from './constants/route.constants';
import PortalProvider from './components/PortalProvider';
import InfoActions from './actions/InfoActions';
import { AppDispatch } from './reducers/store';
import InfoSelectors from './selectors/InfoSelectors';
import QuestionnaireSelectors from './selectors/questionnaires/QuestionnaireSelectors';
import OutputReportSelectors from './selectors/OutputReportSelectors';
import { UserService } from './services/common/UserService';
import useCssVariables from './hooks/useCssVariables';
import useWhitelabelBranding from './hooks/useWhitelabelBranding';
import styles from './App.module.scss';
import LanguageSlice from './common/slices/LanguageSlice';
import UserSlice from './common/slices/UserSlice';
import setLocaleFromUserInfo from './helpers/setLocaleFromUserInfo';
import useOfflineMode from './common/hooks/useOfflineMode';
import { AuthService } from './services/AuthService';
import * as Sentry from '@sentry/react';
import { NotificationActions } from './common/actions/NotificationActions';
import { DEFAULT_LOCALE } from './constants/misc.constants';
import isRunningInIframe from './common/helpers/isRunningInIframe';

const { REACT_APP_FEATURE_SENTRY_USER_FEEDBACK } = import.meta.env;
const FALLBACK_LOCALE = 'en';

let feedbackWidget: any = null;

export default function App() {
    const locale = useSelector(LanguageSlice.state.getLocale);
    const authToken = useSelector(AuthSelectors.getAuthToken);
    const refreshToken = useSelector(AuthSelectors.getRefreshToken);
    const isLoggedIn = useSelector(AuthSelectors.isLoggedIn);

    const info = useSelector(InfoSelectors.getInfo);
    const userInfo = useSelector(UserSlice.state.info);
    const dispatch = useDispatch<AppDispatch>();
    const [requestServiceIsSetup, setRequestServiceIsSetup] = useState(false);
    const language = useSelector(LanguageSlice.state.getAll);
    const logoutRef = useRef(logout);
    const dispatchRef = useRef(dispatch);

    useEffect(() => {
        if (
            !REACT_APP_FEATURE_SENTRY_USER_FEEDBACK ||
            !userInfo.data?.enableBugReport
        ) {
            if (feedbackWidget) {
                removeWidget();
            }
            return;
        }

        if (authToken) {
            const userData = AuthService.decodeUserData(authToken);
            if (userData) {
                Sentry.setUser({
                    id: userData.sub,
                    companyId: userData.companyId,
                    name: `${userData.firstName || ''}${
                        userData.lastName ? ` ${userData.lastName}` : ''
                    }`,
                    email: userData.email || '',
                });
            }
        }

        const feedback = feedbackIntegration({
            autoInject: false,
            showBranding: false,
            colorSchema: 'light',
            useSentryUser: {
                email: 'email',
                name: 'name',
            },
        });
        feedbackWidget = feedback.createWidget();

        return () => {
            removeWidget();
        };

        function removeWidget() {
            feedbackWidget.removeFromDom();
            feedbackWidget = null;
        }
    }, [userInfo.data?.enableBugReport, authToken]);

    function logout() {
        dispatch(AuthActions.logout());
    }

    useEffect(() => {
        RequestService.setup({
            endpoint: import.meta.env.REACT_APP_API_ENDPOINT as string,
            requestParams: import.meta.env
                .REACT_APP_API_ENDPOINT_PARAMS as string,
            locale: locale || DEFAULT_LOCALE,
            accessToken: authToken,
            refreshToken: refreshToken,
            errorLogger: handleError,
            throwErrors: true,
            onRefresh: (tokens) => {
                UserService.setCustomer(
                    tokens.accessToken,
                    tokens.refreshToken,
                );
            },
            onLogout: logoutRef.current,
        });

        setRequestServiceIsSetup(true);
    }, [authToken, refreshToken, locale]);

    function handleError(error: any) {
        if (error?.code === 'ERR_NETWORK') {
            captureMessage(error.message, 'warning');
            return;
        }

        if (error.message === RETRY_LIMIT_ERROR) {
            dispatch(
                NotificationActions.error(
                    'Chyba sítě, zkuste refresh prohlížeče',
                ),
            );
        }

        captureException(error);
    }

    useEffect(() => {
        if (authToken) {
            resolve();
        }

        async function resolve() {
            const userInfo = await dispatch(
                UserSlice.actions.getInfo({ authToken }),
            ).unwrap();

            if (userInfo?.data && locale) {
                setLocaleFromUserInfo(userInfo.data, locale, dispatch);
            }
        }
    }, [authToken]);

    useEffect(() => {
        if (requestServiceIsSetup && authToken && refreshToken) {
            dispatchRef.current(
                AuthActions.selectCustomer(authToken, refreshToken),
            );
        }
    }, [requestServiceIsSetup, authToken, refreshToken]);

    useEffect(() => {
        if (!requestServiceIsSetup) {
            return;
        }

        if (!(authToken && (userInfo.loading || !userInfo.data))) {
            dispatchRef.current(InfoActions.getInfo());
        }
    }, [requestServiceIsSetup, authToken, userInfo.loading, userInfo.data]);

    useEffect(() => {
        if (!requestServiceIsSetup) {
            return;
        }

        if (authToken && !userInfo.loading && !!userInfo.data) {
            dispatchRef.current(UserSlice.actions.getActiveQuestionnaires());
        }
    }, [
        requestServiceIsSetup,
        authToken,
        locale,
        userInfo.loading,
        userInfo.data,
    ]);

    const [, setTheme] = useTheme();
    const setCssVariables = useCssVariables();
    useWhitelabelBranding();
    useEffect(() => {
        if (info.data?.customization) {
            const { primaryColor, secondaryColor } = info.data.customization;

            if (primaryColor || secondaryColor) {
                const newTheme: Partial<Theme> = {};

                if (primaryColor) {
                    newTheme.primaryColor = primaryColor;
                    setCssVariables({ 'primary-color': primaryColor });
                }
                if (secondaryColor) {
                    newTheme.secondaryColor = secondaryColor;
                    setCssVariables({ 'secondary-color': secondaryColor });
                }

                setTheme(newTheme);
            }
        }
    }, [info.data?.customization]);

    const [theme] = useTheme();
    useEffect(() => {
        const root = document.querySelector(':root') as HTMLElement;
        root.style.setProperty('--border-primary-color', theme.borderColor);
        root.style.setProperty('--tcc-primary-color', theme.primaryColor);
        root.style.setProperty('--text-primary-color', theme.textColor);
        root.style.setProperty('--text-inverted-color', theme.textLightColor);
        root.style.setProperty('--text-secondary-color', theme.textGrayColor);
        root.style.setProperty('--surface-primary-color', theme.surfaceColor);
        root.style.setProperty(
            '--surface-secondary-color',
            theme.backgroundColor,
        );
        root.style.setProperty('--error-color', theme.errorColor);
        root.style.setProperty('--success-color', theme.successColor);
        root.style.setProperty('--warning-color', theme.warningColor);
        root.style.setProperty(
            '--text-disabled-color',
            theme.textDisabledColor,
        );
        root.style.setProperty('--surface-disabled-color', theme.disabledColor);
        root.style.setProperty(
            '--border-radius',
            `${theme.borderRadius.toString()}px`,
        );
    }, [theme]);

    if (import.meta.env.REACT_APP_FEATURE_OFFLINE === 'true') {
        useOfflineMode();
    }

    const isIframe: boolean = useMemo((): boolean => isRunningInIframe(), []);

    if (!requestServiceIsSetup) {
        return null;
    }

    if (!locale || info.loading || userInfo.loading || language.loading) {
        return <LoadingOverlay />;
    }

    return (
        <TranslationsProvider
            locale={locale}
            fallbackLocale={FALLBACK_LOCALE}
            version={info.data?.translationVersion}
            loadInfo={false}
        >
            <TranslationsWrapper locale={locale}>
                <NotificationList />
                <RedirectWrapper>
                    <PortalProvider>
                        {!isIframe && (
                            <header className={styles.header}>
                                <Navbar isUserLogged={isLoggedIn} />
                            </header>
                        )}
                        <div
                            className={styles.bg}
                            style={{
                                backgroundImage: info.data?.customization
                                    ?.backgroundImage
                                    ? `url(${info.data?.customization?.backgroundImage})`
                                    : '',
                            }}
                        />
                        <main>
                            <QuestionnaireTimer />
                            <GlobalLoading />
                            <AppRoutes />
                        </main>
                    </PortalProvider>
                </RedirectWrapper>
            </TranslationsWrapper>
        </TranslationsProvider>
    );
}

function RedirectWrapper(props: { children: ReactNode }) {
    const shouldRedirectToQuestionnaire = useSelector(
        AuthSelectors.shouldRedirectToQuestionnaire,
    );
    const activeQuestionnaire = useSelector(
        UserSlice.state.activeQuestionnaire,
    );
    const hasLoggedOut = useSelector(AuthSelectors.hasLoggedOut);
    const navigate = useNavigate();

    useEffect(() => {
        if (shouldRedirectToQuestionnaire && activeQuestionnaire) {
            navigate(
                generatePath(QUESTIONNAIRE_PATH, {
                    id: activeQuestionnaire.id,
                }),
            );
        }
    }, [shouldRedirectToQuestionnaire, activeQuestionnaire, history]);

    useEffect(() => {
        if (hasLoggedOut) {
            navigate(SIGN_IN_PATH, { replace: true, relative: 'path' });
        }
    }, [hasLoggedOut]);

    return <>{props.children}</>;
}

function TranslationsWrapper(props: { locale: string; children: ReactNode }) {
    const { locale, children } = props;
    const { isLoading, translations } = useTranslations('fe');

    if (isLoading) {
        return <LoadingOverlay />;
    }

    return (
        <TranslationProvider
            translations={translations}
            locale={locale}
            options={{
                interpolation: { prefix: '{', suffix: '}' },
                fallbackLocale: FALLBACK_LOCALE,
                fallbackValue: null, // import.meta.env.PROD ? ' ' : null,
            }}
        >
            {children}
        </TranslationProvider>
    );
}

function GlobalLoading() {
    const authLoading = useSelector(AuthSelectors.isLoading);
    const questionnaireLoading = useSelector(QuestionnaireSelectors.isLoading);
    const outputReportLoading = useSelector(
        OutputReportSelectors.getDetailLoading,
    );

    return (
        <Loading
            isLoading={
                authLoading || questionnaireLoading || outputReportLoading
            }
        />
    );
}
